import * as Sentry from '@sentry/react';
import { resolveWebTraceSampleRate } from './sentry.sampling';
import { syncSentryUserContext } from './sentry.tags';

export interface WebSentryEnv {
  app: string;
  dsn: string;
  environment: string;
  release: string;
  apiBaseUrl?: string;
  tracesSampleRate: number | string;
  replaysSessionSampleRate: number | string;
  replaysOnErrorSampleRate: number | string;
}

function toNumber(value: number | string, fallback: number): number {
  if (value === '') {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function redactSensitiveValues(value: unknown): unknown {
  if (typeof value === 'string')
    return value.replace(/asmcp_(?:at|rt|code)_[A-Za-z0-9_-]+/g, '[REDACTED]');
  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveValues(item));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
    (accumulator, [key, nestedValue]) => {
      const normalizedKey = key.toLowerCase();
      if (
        /^(?:code|code_verifier|code_challenge|state|redirecturl)$/.test(normalizedKey) ||
        normalizedKey.includes('authorization') ||
        normalizedKey.includes('cookie') ||
        normalizedKey.includes('token') ||
        normalizedKey.includes('secret') ||
        normalizedKey.includes('password')
      ) {
        accumulator[key] = '[REDACTED]';
        return accumulator;
      }

      accumulator[key] = redactSensitiveValues(nestedValue);
      return accumulator;
    },
    {},
  );
}

function buildDefaultTags(env: Pick<WebSentryEnv, 'app' | 'environment' | 'release'>) {
  return {
    app: env.app,
    environment: env.environment,
    release: env.release || 'local',
    platform: 'web',
  };
}

function buildTracePropagationTargets(apiBaseUrl?: string): Array<string | RegExp> {
  const targets = new Set<string | RegExp>(['localhost']);

  if (!apiBaseUrl) {
    return Array.from(targets);
  }

  try {
    const parsed = new URL(apiBaseUrl);
    targets.add(parsed.origin);

    const normalizedPath = parsed.pathname.replace(/\/$/, '');
    if (normalizedPath && normalizedPath !== '/') {
      targets.add(`${parsed.origin}${normalizedPath}`);
    }
  } catch {
    targets.add(apiBaseUrl.replace(/\/$/, ''));
  }

  return Array.from(targets);
}

export function createWebSentryOptions(env: WebSentryEnv): Sentry.BrowserOptions | null {
  if (!env.dsn) {
    return null;
  }

  const tracesSampleRate = toNumber(env.tracesSampleRate, 0.02);

  return {
    dsn: env.dsn,
    environment: env.environment,
    release: env.release || undefined,
    sendDefaultPii: true,
    tracesSampleRate,
    tracesSampler: (samplingContext) =>
      resolveWebTraceSampleRate(tracesSampleRate, samplingContext),
    replaysSessionSampleRate: toNumber(env.replaysSessionSampleRate, 0.05),
    replaysOnErrorSampleRate: toNumber(env.replaysOnErrorSampleRate, 1),
    tracePropagationTargets: buildTracePropagationTargets(env.apiBaseUrl),
    integrations: [
      Sentry.browserTracingIntegration({
        traceFetch: false,
        traceXHR: false,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    beforeSend(event) {
      return redactSensitiveValues(event) as typeof event;
    },
    initialScope: {
      tags: buildDefaultTags(env),
    },
  };
}

export function initWebSentry(env: WebSentryEnv, options?: { router?: unknown }): boolean {
  const sentryOptions = createWebSentryOptions(env);

  if (!sentryOptions) {
    return false;
  }

  // createWebSentryOptions sempre define integrations como array (o tipo também admite função)
  const integrations = [...(sentryOptions.integrations as Extract<typeof sentryOptions.integrations, unknown[]>)];
  if (options?.router) {
    integrations.push(Sentry.tanstackRouterBrowserTracingIntegration(options.router));
  }

  Sentry.init({
    ...sentryOptions,
    integrations,
  });
  Sentry.setTags(buildDefaultTags(env));
  syncSentryUserContext();
  return true;
}
